import { useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import TaskInviteTokenService from '@/services/taskInviteToken.service';
import { TasksPage } from '@/pages/TasksPage';
import { FullPagerPortal } from '@/modules/shared/components/FullPager';

/**
 * /review/:orgKey/tasks
 *
 * Reviewer-only inbox. No AppShell sidebar/tab-bar. Reuses the same
 * `TasksPage` component the org user sees — the only difference is no
 * surrounding chrome.
 *
 * If the URL also has `?fp=...&task=...` (deep-link from an invite email),
 * FullPagerPortal auto-opens to the right report.
 *
 * Bounces back to /review/:orgKey if no invite session is active.
 */
export function ReviewerInboxPage() {
    const { orgKey = '' } = useParams<{ orgKey: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const reviewerEmail = TaskInviteTokenService.getEmail();

    useEffect(() => {
        if (!TaskInviteTokenService.isActive()) {
            const next = `${window.location.pathname}${window.location.search}`;
            navigate(`/review/${encodeURIComponent(orgKey)}?next=${encodeURIComponent(next)}`, {
                replace: true,
            });
        }
    }, [navigate, orgKey]);

    const showPager = useMemo(() => Boolean(searchParams.get('fp')), [searchParams]);

    if (!TaskInviteTokenService.isActive()) return null;

    return (
        <div
            className="min-h-screen flex flex-col"
            style={{ background: 'var(--canvas, #FAFAFA)' }}
        >
            <header
                className="flex items-center justify-between px-6 h-12 border-b"
                style={{
                    background: 'var(--surface-primary, #fff)',
                    borderColor: 'var(--border-default)',
                }}
            >
                <div className="flex items-center gap-3 text-[13px]">
                    <strong style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                        Reviews
                    </strong>
                    {reviewerEmail && (
                        <span style={{ color: 'var(--text-tertiary)' }}>· {reviewerEmail}</span>
                    )}
                </div>
                <button
                    type="button"
                    className="text-[12.5px]"
                    style={{ color: 'var(--text-tertiary)' }}
                    onClick={async () => {
                        try {
                            await fetch('/api/v2/tasks/auth/logout', {
                                method: 'POST',
                                headers: { Authorization: TaskInviteTokenService.getAuthHeader() || '' },
                            });
                        } catch {
                            /* ignore */
                        }
                        TaskInviteTokenService.clear();
                        navigate(`/review/${encodeURIComponent(orgKey)}`, { replace: true });
                    }}
                >
                    Sign out
                </button>
            </header>
            <main className="flex-1 min-h-0">
                <TasksPage />
            </main>
            {/* Same FullPager portal the org-user shell mounts — picks up
                ?fp=...&task=... from the URL and renders CandidateReport. */}
            {showPager && <FullPagerPortal />}
        </div>
    );
}

export default ReviewerInboxPage;
